import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { lookupEmail, linkContactoByPhone } from "@/lib/auth-lookup.functions";
import { saveOnboarding, saveOnboardingByEmail, countMyAnuncios } from "@/lib/onboarding.functions";
import { signUpAndSendConfirmation, signUpAndSendOtp, sendPasswordResetEmail } from "@/lib/auth-email.functions";
import { useNavigate } from "@tanstack/react-router";
import { Search, Megaphone, Building2, HelpCircle, User, Briefcase, Network, EyeOff, Plus, ListChecks } from "lucide-react";

type Step =
  | "email"
  | "login"
  | "signup-known"
  | "signup-new"
  | "verify-otp"
  | "forgot-sent"
  | "onboarding-usage"
  | "onboarding-perfil"
  | "onboarding-anunciante";


type UsageTipo = "demandante" | "anunciante" | "agencia" | "curioso";
type PerfilTipo = "particular" | "empresario" | "franquicia" | "prefiere_no_decirlo";

function OnboardingOption({
  icon,
  title,
  subtitle,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-start gap-3 rounded-lg border border-border bg-card p-3 text-left transition hover:border-primary hover:bg-accent disabled:opacity-50"
    >
      <span className="mt-0.5 text-primary">{icon}</span>
      <span className="flex flex-col">
        <span className="text-sm font-medium">{title}</span>
        {subtitle && <span className="text-xs text-muted-foreground">{subtitle}</span>}
      </span>
    </button>
  );
}

export function AuthDialog({
  open,
  onOpenChange,
  initialStep,
  inline,
  onComplete,
  onSubStep,
  onEmailContinue,
  emailTitle = "Acceder a Bizaclia",
  emailSubmitLabel = "Continuar",
  prefillEmail,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  initialStep?: "onboarding-usage";
  inline?: boolean;
  onComplete?: () => void;
  onEmailContinue?: (email: string) => void;
  emailTitle?: string;
  emailSubmitLabel?: string;
  /** Email ya conocido (p. ej. del conector light): se salta el paso del correo. */
  prefillEmail?: string | null;
  /** Notifica sub-pasos internos del flujo auth (email/login/register/verify_code/success). */
  onSubStep?: (sub: "email" | "login" | "register" | "verify_code" | "success") => void;
}) {
  const [step, setStep] = useState<Step>(initialStep ?? "email");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nombre, setNombre] = useState("");
  const [apellidos, setApellidos] = useState("");
  const [telefono, setTelefono] = useState("");
  const [knownName, setKnownName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [misAnuncios, setMisAnuncios] = useState<number>(0);
  const [loginError, setLoginError] = useState<string>("");
  const [phoneError, setPhoneError] = useState<string>("");
  const [otpCode, setOtpCode] = useState<string>("");
  const [otpError, setOtpError] = useState<string>("");
  const [needsConfirm, setNeedsConfirm] = useState(false);
  const navigate = useNavigate();

  // Si se abre forzando onboarding, asegurar el paso inicial.
  useEffect(() => {
    if (open && initialStep) {
      setStep(initialStep);
    }
  }, [open, initialStep]);

  const isOnboardingStep =
    step === "onboarding-usage" ||
    step === "onboarding-perfil" ||
    step === "onboarding-anunciante";

  const reset = () => {
    prefillUsado.current = false;
    setStep("email");
    setPassword("");
    setNombre("");
    setApellidos("");
    setTelefono("");
    setKnownName(null);
    setMisAnuncios(0);
    setLoginError("");
    setPhoneError("");
    setOtpCode("");
    setOtpError("");
    setNeedsConfirm(false);
  };


  // Cierre forzoso (lo usan los handlers tras completar onboarding o login).
  const forceClose = () => {
    reset();
    setEmail("");
    onOpenChange?.(false);
    onComplete?.();
  };

  const close = (next: boolean) => {
    if (!next && isOnboardingStep) {
      // No permitir cerrar mientras el onboarding está activo.
      return;
    }
    if (!next) {
      reset();
      setEmail("");
    }
    onOpenChange?.(next);
  };


  const handleEmailStep = async (e: FormEvent) => {
    e.preventDefault();
    await continuarConEmail(email);
  };

  // Con un email ya conocido se pasa directamente a entrar o a crear la
  // contraseña, con el nombre prerrellenado si ya era un contacto.
  const prefillUsado = useRef(false);
  useEffect(() => {
    if (!open || !prefillEmail || prefillUsado.current || step !== "email") return;
    prefillUsado.current = true;
    setEmail(prefillEmail);
    void continuarConEmail(prefillEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefillEmail]);

  const continuarConEmail = async (email: string) => {
    setLoading(true);
    try {
      const result = await lookupEmail({ data: { email } });
      onEmailContinue?.(email);
      if (result.userExists) {
        onSubStep?.("login");
        setStep("login");
      } else if (result.knownContact) {
        const fullName = [result.knownContact.nombre, result.knownContact.apellidos]
          .filter(Boolean)
          .join(" ")
          .trim();
        setKnownName(fullName || null);
        setNombre(result.knownContact.nombre ?? "");
        setApellidos(result.knownContact.apellidos ?? "");
        onSubStep?.("register");
        setStep("signup-known");
      } else {
        setNombre("");
        setApellidos("");
        onSubStep?.("register");
        setStep("signup-new");
      }
    } catch (err) {
      toast.error("No se pudo verificar el correo. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setLoginError("");
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) {
      const msg = error.message.toLowerCase();
      let translated = "Error al iniciar sesión. Inténtalo de nuevo.";
      if (msg.includes("invalid login credentials")) translated = "Contraseña incorrecta.";
      else if (msg.includes("email not confirmed")) translated = "Correo no confirmado. Revisa tu bandeja de entrada.";
      else if (msg.includes("too many requests")) translated = "Demasiados intentos. Espera un momento.";
      setLoginError(translated);
    } else {
      onSubStep?.("success");
      forceClose();
      window.dispatchEvent(new Event("bizaclia:login-complete"));
    }
  };


  const handleSignup = async (e: FormEvent) => {
    e.preventDefault();
    setPhoneError("");
    // El teléfono solo se pide en signup-new (contacto desconocido).
    if (step === "signup-new") {
      const isSpanish = /^(?:(?:\+|00)34)?[6789]\d{8}$/.test(telefono.replace(/[\s\-]/g, ""));
      const isIntl = /^\+\d{6,15}$/.test(telefono.replace(/[\s\-]/g, ""));
      if (!isSpanish && !isIntl) {
        setPhoneError("Introduce un teléfono válido.");
        return;
      }
    }
    setLoading(true);
    const { getStoredUtms } = await import("@/lib/utm");
    const utms = getStoredUtms();
    const signupPayload = {
      data: {
        email,
        password,
        metadata: {
          nombre: nombre || undefined,
          apellidos: apellidos || undefined,
          telefono: telefono || undefined,
          ...utms,
        },
      },
    };

    const result = inline
      ? await signUpAndSendOtp(signupPayload).catch((err: unknown) => {
          const msg = err instanceof Error ? err.message : "Error al registrar";
          toast.error(msg);
          return null;
        })
      : await signUpAndSendConfirmation(signupPayload).catch((err: unknown) => {
          const msg = err instanceof Error ? err.message : "Error al registrar";
          toast.error(msg);
          return null;
        });
    if (!result) {
      setLoading(false);
      return;
    }
    if ("alreadyExists" in result && result.alreadyExists) {
      setLoading(false);
      toast.error("Este email ya está registrado. Inicia sesión.");
      setStep("login");
      return;
    }
    setLoading(false);
    try {
      const { trackConversion } = await import("@/lib/tracking");
      // userId aún no existe (email pendiente de confirmación); usamos el email
      // como identificador estable y también para user matching en Meta CAPI.
      trackConversion("sign_up", { userId: email, email });
    } catch {
      // silencioso
    }
    if (inline) {
      toast.success("Te hemos enviado un código a tu correo.");
      setOtpCode("");
      setOtpError("");
      onSubStep?.("verify_code");
      setStep("verify-otp");
      return;
    }
    toast.success("Cuenta creada. Revisa tu correo para confirmar.");
    setNeedsConfirm(true);
    setStep("onboarding-usage");
  };

  const handleVerifyOtp = async (e: FormEvent) => {
    e.preventDefault();
    setOtpError("");
    const token = otpCode.replace(/\D/g, "");
    if (token.length !== 6) {
      setOtpError("Introduce los 6 dígitos.");
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: "email",
    });
    setLoading(false);
    if (error) {
      const msg = error.message.toLowerCase();
      if (msg.includes("expired")) setOtpError("El código ha caducado. Solicita uno nuevo.");
      else if (msg.includes("invalid") || msg.includes("token")) setOtpError("Código incorrecto.");
      else setOtpError("No se pudo verificar. Inténtalo de nuevo.");
      return;
    }
    window.dispatchEvent(new Event("bizaclia:login-complete"));
    onSubStep?.("success");
    forceClose();
  };


  const handleUsage = async (tipo: UsageTipo) => {
    setLoading(true);
    try {
      if (needsConfirm) {
        await saveOnboardingByEmail({ data: { email, tipo } });
      } else {
        await saveOnboarding({ data: { tipo } });
      }
    } catch {
      // silencioso
    }
    if (tipo === "demandante") {
      setLoading(false);
      setStep("onboarding-perfil");
      return;
    }
    if (tipo === "anunciante" && !needsConfirm) {
      try {
        const { count } = await countMyAnuncios();
        setMisAnuncios(count);
      } catch {
        setMisAnuncios(0);
      }
      setLoading(false);
      setStep("onboarding-anunciante");
      return;
    }
    setLoading(false);
    forceClose();
  };

  const handlePerfil = async (perfil: PerfilTipo) => {
    setLoading(true);
    try {
      if (needsConfirm) {
        await saveOnboardingByEmail({ data: { email, tipo: "demandante", perfil } });
      } else {
        await saveOnboarding({ data: { tipo: "demandante", perfil } });
      }
    } catch {
      // silencioso
    }
    setLoading(false);
    forceClose();
  };

  const handleForgot = async () => {
    if (!email) return;
    setLoading(true);
    const result = await sendPasswordResetEmail({ data: { email } }).catch(
      () => null,
    );
    setLoading(false);
    if (!result || !result.ok) {
      toast.error("No se pudo enviar el correo. Inténtalo de nuevo.");
    } else {
      setStep("forgot-sent");
    }
  };

  const BackToEmail = () => (
    <button
      type="button"
      onClick={reset}
      className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-xs text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-3 w-3" /> cambiar correo
    </button>
  );

  const AuthHeader = inline
    ? ({ children }: { children: ReactNode }) => (
        <div className="mb-4 flex flex-col space-y-1.5 text-left">{children}</div>
      )
    : DialogHeader;
  const AuthTitle = inline
    ? ({ children }: { children: ReactNode }) => (
        <h2 className="text-lg font-semibold leading-none tracking-tight">{children}</h2>
      )
    : DialogTitle;
  const AuthDescription = inline
    ? ({ children, className }: { children: ReactNode; className?: string }) => (
        <p className={`text-sm text-muted-foreground ${className ?? ""}`}>{children}</p>
      )
    : DialogDescription;

  const body = (
    <>
      {inline && <div className="sr-only" aria-hidden />}

        {step === "email" && (
          <>
            <AuthHeader>
              <AuthTitle>{emailTitle}</AuthTitle>
              <AuthDescription>Introduce tu correo para continuar.</AuthDescription>
            </AuthHeader>
            <form onSubmit={handleEmailStep} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="auth-email">Correo</Label>
                <Input
                  id="auth-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Comprobando…" : emailSubmitLabel}
              </Button>
            </form>
          </>
        )}

        {step === "login" && (
          <>
            <AuthHeader>
              <AuthTitle>Hola de nuevo</AuthTitle>
              <AuthDescription className="text-left">
                <span className="whitespace-nowrap">{email}</span> <BackToEmail />
              </AuthDescription>
            </AuthHeader>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="auth-password">Contraseña</Label>
                <PasswordInput
                  id="auth-password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setLoginError("");
                  }}
                  required
                  autoFocus
                />
                {loginError && (
                  <p className="text-sm text-destructive">{loginError}</p>
                )}
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Entrando…" : "Entrar"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full text-sm"
                onClick={handleForgot}
                disabled={loading}
              >
                ¿Olvidaste tu contraseña?
              </Button>
            </form>
          </>
        )}

        {step === "signup-known" && (
          <>
            <AuthHeader>
              <AuthTitle>{knownName ? `Hola ${knownName}` : "Te conocemos"}</AuthTitle>
              <AuthDescription className="text-left">
                Crea una contraseña para acceder con <span className="whitespace-nowrap">{email}</span> <BackToEmail />
              </AuthDescription>
            </AuthHeader>
            <form onSubmit={handleSignup} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="auth-password-known">Contraseña</Label>
                <PasswordInput
                  id="auth-password-known"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  autoFocus
                />
              </div>
              {!apellidos && (
                <div className="space-y-2">
                  <Label htmlFor="auth-apellidos-known">Apellidos</Label>
                  <Input
                    id="auth-apellidos-known"
                    type="text"
                    autoComplete="family-name"
                    value={apellidos}
                    onChange={(e) => setApellidos(e.target.value)}
                  />
                </div>
              )}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Creando…" : "Crear cuenta"}
              </Button>
            </form>
          </>
        )}

        {step === "signup-new" && (
          <>
            <AuthHeader>
              <AuthTitle>Crea tu cuenta</AuthTitle>
              <AuthDescription className="text-left">
                <span className="whitespace-nowrap">{email}</span> <BackToEmail />
              </AuthDescription>
            </AuthHeader>
            <form onSubmit={handleSignup} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="auth-name">Nombre</Label>
                  <Input
                    id="auth-name"
                    type="text"
                    autoComplete="given-name"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="auth-apellidos">Apellidos</Label>
                  <Input
                    id="auth-apellidos"
                    type="text"
                    autoComplete="family-name"
                    value={apellidos}
                    onChange={(e) => setApellidos(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="auth-phone">Teléfono</Label>
                <Input
                  id="auth-phone"
                  type="tel"
                  autoComplete="tel"
                  value={telefono}
                  onChange={(e) => {
                    setTelefono(e.target.value);
                    setPhoneError("");
                  }}
                  required
                />
                {phoneError && (
                  <p className="text-sm text-destructive">{phoneError}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="auth-password-new">Contraseña</Label>
                <PasswordInput
                  id="auth-password-new"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Creando…" : "Crear cuenta"}
              </Button>
            </form>
          </>
        )}

        {step === "verify-otp" && (
          <>
            <AuthHeader>
              <AuthTitle>Verifica tu correo</AuthTitle>
              <AuthDescription className="text-left">
                Introduce el código de 6 dígitos que hemos enviado a{" "}
                <span className="whitespace-nowrap font-medium text-foreground">{email}</span>.
              </AuthDescription>
            </AuthHeader>
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="auth-otp">Código</Label>
                <Input
                  id="auth-otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => {
                    setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                    setOtpError("");
                  }}
                  className="text-center text-2xl tracking-[0.4em] font-semibold"
                  required
                  autoFocus
                />
                {otpError && <p className="text-sm text-destructive">{otpError}</p>}
              </div>
              <Button type="submit" className="w-full" disabled={loading || otpCode.length !== 6}>
                {loading ? "Verificando…" : "Verificar y continuar"}
              </Button>
            </form>
          </>
        )}


        {step === "forgot-sent" && (
          <>
            <AuthHeader>
              <AuthTitle>Revisa tu correo</AuthTitle>
              <AuthDescription>
                Te hemos enviado un enlace a {email} para restablecer tu contraseña.
              </AuthDescription>
            </AuthHeader>
            <Button onClick={() => close(false)} className="w-full">
              Entendido
            </Button>
          </>
        )}

        {step === "onboarding-usage" && (
          <>
            <AuthHeader>
              <AuthTitle>¿Cómo usarás Bizaclia?</AuthTitle>
              <AuthDescription>
                Personalizaremos tu experiencia según tu respuesta.
              </AuthDescription>
            </AuthHeader>
            <div className="grid gap-2">
              <OnboardingOption
                icon={<Search className="h-5 w-5" />}
                title="Busco locales de negocio"
                onClick={() => handleUsage("demandante")}
                disabled={loading}
              />
              <OnboardingOption
                icon={<Megaphone className="h-5 w-5" />}
                title="Quiero publicar locales"
                onClick={() => handleUsage("anunciante")}
                disabled={loading}
              />
              <OnboardingOption
                icon={<Building2 className="h-5 w-5" />}
                title="Represento a una agencia inmobiliaria"
                onClick={() => handleUsage("agencia")}
                disabled={loading}
              />
              <OnboardingOption
                icon={<HelpCircle className="h-5 w-5" />}
                title="Prefiero no responder"
                onClick={() => handleUsage("curioso")}
                disabled={loading}
              />
            </div>
          </>
        )}

        {step === "onboarding-perfil" && (
          <>
            <AuthHeader>
              <AuthTitle>¿Cuál es tu perfil?</AuthTitle>
              <AuthDescription>
                Nos ayuda a recomendarte mejores oportunidades.
              </AuthDescription>
            </AuthHeader>
            <div className="grid gap-2">
              <OnboardingOption
                icon={<User className="h-5 w-5" />}
                title="Soy un particular"
                subtitle="Emprendiendo por primera vez."
                onClick={() => handlePerfil("particular")}
                disabled={loading}
              />
              <OnboardingOption
                icon={<Briefcase className="h-5 w-5" />}
                title="Soy empresario/autónomo"
                subtitle="Ya tengo un negocio."
                onClick={() => handlePerfil("empresario")}
                disabled={loading}
              />
              <OnboardingOption
                icon={<Network className="h-5 w-5" />}
                title="Represento un grupo de expansión / franquicia"
                onClick={() => handlePerfil("franquicia")}
                disabled={loading}
              />
              <OnboardingOption
                icon={<EyeOff className="h-5 w-5" />}
                title="Prefiero no decirlo"
                onClick={() => handlePerfil("prefiere_no_decirlo")}
                disabled={loading}
              />
            </div>
          </>
        )}

        {step === "onboarding-anunciante" && (
          <>
            <AuthHeader>
              <AuthTitle>¡Listo para publicar!</AuthTitle>
              <AuthDescription>
                {misAnuncios > 0
                  ? `Tienes ${misAnuncios} anuncio${misAnuncios === 1 ? "" : "s"} publicado${misAnuncios === 1 ? "" : "s"}.`
                  : "Empieza publicando tu primer anuncio."}
              </AuthDescription>
            </AuthHeader>
            <div className="grid gap-2">
              {misAnuncios > 0 && (
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2"
                  onClick={() => {
                    forceClose();
                    navigate({ to: "/admin/anuncios" });
                  }}
                >
                  <ListChecks className="h-4 w-4" />
                  Ver mis anuncios actuales
                </Button>
              )}
              <Button
                className="w-full justify-start gap-2"
                onClick={() => {
                  forceClose();
                  navigate({ to: "/admin/anuncios" });
                }}
              >
                <Plus className="h-4 w-4" />
                Publicar anuncio
              </Button>
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => forceClose()}
              >
                Cerrar ventana
              </Button>
            </div>
          </>
        )}
    </>
  );

  if (inline) {
    return <div className="text-left">{body}</div>;
  }

  return (
    <Dialog open={open ?? false} onOpenChange={close}>
      <DialogContent
        className={`sm:max-w-md ${isOnboardingStep ? "[&>button]:hidden" : ""}`}
        onEscapeKeyDown={(e) => {
          if (isOnboardingStep) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (isOnboardingStep) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (isOnboardingStep) e.preventDefault();
        }}
      >
        {body}
      </DialogContent>
    </Dialog>
  );

}
